import { useState, useEffect, useCallback } from "react";

import CentralHub from "./CentralHub";
import { features } from "./Data";
import ListFeatureCard from "./ListFeatureCard";
import RadialFeatureCard from "./RadialFeatureCard";
import Background from "./Background";

const defaultHubContent = {
  title: (
    <>
      Why
      <br />
      Choose
      <br />
      Drone Planet?
    </>
  ),
  icon: null,
};

const useWindowSize = () => {
  const [size, setSize] = useState([0, 0]);
  useEffect(() => {
    let timeoutId = null;
    const resizeListener = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(
        () => setSize([window.innerWidth, window.innerHeight]),
        150
      );
    };
    window.addEventListener("resize", resizeListener);
    resizeListener();
    return () => window.removeEventListener("resize", resizeListener);
  }, []);
  return { width: size[0], height: size[1] };
};

const SectionTitle = () => (
  <div className="text-center mb-4 sm:mb-8">
    <h2 className="text-3xl sm:text-4xl lg:text-4xl font-extrabold text-slate-700 tracking-tight">
      Why Choose{" "}
      <span className="relative inline-block">
        <span className="absolute inset-0 bg-sky-200/50 -skew-y-2 transform rounded-lg"></span>
        <span className="relative text-sky-600">Drone Planet? </span>
      </span>{" "}
    </h2>
  </div>
);

const FeatureSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const { width, height } = useWindowSize();
  const isExtraLargeScreen = width >= 1280;

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const getCardPosition = useCallback(
    (index, total) => {
      const angle = (index / total) * (2 * Math.PI);
      const radius = Math.min(width * 0.28, height * 0.38, 450);
      const x = Math.round(radius * Math.cos(angle));
      const y = Math.round(radius * Math.sin(angle));
      return { x, y };
    },
    [width, height]
  );

  const handleCardClick = (feature) => {
    if (selectedFeature?.id === feature.id) {
      setSelectedFeature(null);
    } else {
      setSelectedFeature(feature);
    }
  };

  const handleMouseEnter = (feature) => setHoveredFeature(feature);
  const handleMouseLeave = () => setHoveredFeature(null);

  const getHubContent = () => {
    const feature = selectedFeature || hoveredFeature;
    if (feature) return { title: feature.title, icon: feature.icon };
    return defaultHubContent;
  };

  return (
    <div className="relative flex flex-col justify-center items-center w-full min-h-fit xl:min-h-screen p-4 sm:p-6 md:p-8 lg:p-12 font-sans mb-4">
      <Background />

      {isExtraLargeScreen ? (
        <section
          className="relative flex items-center justify-center h-full w-full z-10 flex-grow"
          style={{ perspective: "2000px", transformStyle: "preserve-3d" }}
        >
          <CentralHub content={getHubContent()} onDeselect={() => setSelectedFeature(null)} />
          {features.map((feature, index) => {
            const { x, y } = getCardPosition(index, features.length);
            return (
              <RadialFeatureCard
                key={feature.id}
                feature={feature}
                style={{ transform: `translate3d(${x}px, ${y}px, 0)` }}
                index={index}
                isVisible={isVisible}
                onMouseEnter={() => handleMouseEnter(feature)}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleCardClick(feature)}
                isSelected={selectedFeature?.id === feature.id}
              />
            );
          })}
        </section>
      ) : (
        <section className="w-full max-w-6xl z-10">
          <SectionTitle />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <ListFeatureCard
                key={feature.id}
                feature={feature}
                index={index}
                isVisible={isVisible}
                onClick={() => handleCardClick(feature)}
                isSelected={selectedFeature?.id === feature.id}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default FeatureSection;
